import { useState, useEffect, useCallback } from "react";
import { arrayMove } from "@dnd-kit/sortable";

const STORAGE_PREFIX = "codarx_table_columns_";

export const getColumnKey = (col) =>
  String(col.key || col.dataIndex || col.title || "");

const readStoredOrder = (storageKey, columns) => {
  if (!storageKey || !columns?.length) return columns || [];
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + storageKey);
    if (!raw) return columns;
    const savedKeys = JSON.parse(raw);
    if (!Array.isArray(savedKeys) || !savedKeys.length) return columns;

    const colMap = new Map(columns.map((col) => [getColumnKey(col), col]));
    const ordered = savedKeys
      .filter((key) => colMap.has(key))
      .map((key) => colMap.get(key));
    const savedSet = new Set(savedKeys);
    // Append any newly-added columns not present in the saved order
    const newCols = columns.filter((col) => !savedSet.has(getColumnKey(col)));
    return [...ordered, ...newCols];
  } catch {
    return columns;
  }
};

const hasSavedOrder = (storageKey) => {
  if (!storageKey) return false;
  try {
    return !!localStorage.getItem(STORAGE_PREFIX + storageKey);
  } catch {
    return false;
  }
};

export const useColumnOrder = (storageKey, columns) => {
  const [orderedColumns, setOrderedColumns] = useState(() =>
    readStoredOrder(storageKey, columns),
  );
  const [hasCustomOrder, setHasCustomOrder] = useState(() =>
    hasSavedOrder(storageKey),
  );

  useEffect(() => {
    setOrderedColumns(readStoredOrder(storageKey, columns));
    setHasCustomOrder(hasSavedOrder(storageKey));
  }, [storageKey, columns?.length]);

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return;
      setOrderedColumns((prev) => {
        const from = prev.findIndex((col) => getColumnKey(col) === active.id);
        const to = prev.findIndex((col) => getColumnKey(col) === over.id);
        if (from === -1 || to === -1) return prev;

        const next = arrayMove(prev, from, to);

        if (storageKey) {
          try {
            localStorage.setItem(
              STORAGE_PREFIX + storageKey,
              JSON.stringify(next.map(getColumnKey)),
            );
            setHasCustomOrder(true);
          } catch {}
        }
        return next;
      });
    },
    [storageKey],
  );

  const resetOrder = useCallback(() => {
    if (storageKey) {
      try {
        localStorage.removeItem(STORAGE_PREFIX + storageKey);
      } catch {}
    }
    setOrderedColumns(columns || []);
    setHasCustomOrder(false);
  }, [storageKey, columns]);

  return { orderedColumns, handleDragEnd, resetOrder, hasCustomOrder };
};
